import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck, Sparkles, Users } from 'lucide-react';
import Metadata from '../components/Metadata';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import { assets, company, pageMeta } from '../data/siteData';
import { assetSrcSet, assetUrl } from '../lib/media';

export default function AboutPage() {
  return (
    <>
      <Metadata
        title={pageMeta.about.title}
        description={pageMeta.about.description}
        image={assetUrl(pageMeta.about.ogImage, 1200)}
        path="/om-rs-byg"
      />

      <PageHero
        eyebrow="Om RS Byg"
        title="Et moderne dansk håndværkerbrand med fokus på troværdighed og kvalitet"
        description="RS Byg er skabt til kunder, der ønsker et professionelt resultat og en proces, hvor der bliver fulgt op på aftaler, detaljer og finish."
        image="https://assets.auxodev.dk/assets/cc07307d-38f2-4d7e-9eca-ab76959f9adc/toemrer-arbejde-detalje.jpg"
        primaryAction={{ label: 'Kontakt os', to: '/kontakt' }}
        secondaryAction={{ label: 'Se galleri', to: '/galleri' }}
        stats={[
          { label: 'Erfaring', value: 'Vi arbejder med opgaver, hvor der kræves overblik, håndværksmæssig præcision og respekt for boligens udtryk.' },
          { label: 'Kvalitet', value: 'Detaljer, samlinger og materialer er en vigtig del af det færdige resultat.' },
          { label: 'Lokalt engagement', value: 'Vi prioriterer kunder i Thisted og omegn og kender de behov, som lokale projekter typisk kræver.' },
        ]}
      />

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">

          <div className="lg:col-span-2">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <SectionHeading
                  eyebrow="Vores tilgang"
                  title="Vi arbejder ordentligt, tydeligt og med respekt for dit hjem"
                  description="Det skal være trygt at vælge en håndværker. Derfor vægter vi den tætte dialog, stor ryddelighed på pladsen og en kompromisløs finish, der harmonerer perfekt med din boligs eksisterende detaljer og kvalitet."
                />

                <div className="mt-6 max-w-3xl text-base leading-8 text-brand-gray-600 sm:text-lg space-y-4">
                  <p>
                    Som tømrerfirma udfører vi alt indenfor tømrerfaget, men vi har en særlig passion for projekter, der kræver ekstra præcision – herunder <strong>smukke træterrasser, gennemgribende renoveringer, professionel tagudskiftning samt montering af nye vinduer og døre</strong>.
                  </p>
                  <p>
                    RS Byg er bygget på en dyb faglig stolthed og en forkærlighed for detaljen. For os er det afgørende, at hver enkelt samling og afslutning sidder lige i skabet, så du får et solidt og æstetisk resultat, du kan glæde dig over i mange år fremover.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center lg:col-span-4 bg-brand-gray-50 border border-brand-gray-200 p-8 h-full min-h-[250px]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/cc07307d-38f2-4d7e-9eca-ab76959f9adc/rs-byg-logo.png"
                  alt="RS BYG Logo"
                  className="max-h-40 w-auto object-contain"
                />
              </div>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              <article className="border border-brand-gray-200 bg-brand-gray-50 p-5">
                <div className="flex h-12 w-12 items-center justify-center bg-brand-black text-white">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-brand-black">Fokus på kundetilfredshed</h3>
                <p className="mt-2 text-sm leading-7 text-brand-gray-600">Vi ønsker, at du står tilbage med et resultat, du kan være glad for i mange år.</p>
              </article>

              <article className="border border-brand-gray-200 bg-brand-gray-50 p-5">
                <div className="flex h-12 w-12 items-center justify-center bg-brand-black text-white">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-brand-black">Solid kvalitet</h3>
                <p className="mt-2 text-sm leading-7 text-brand-gray-600">Vi leverer løsninger, hvor konstruktion, finish og materialer arbejder sammen.</p>
              </article>

              <article className="border border-brand-gray-200 bg-brand-gray-50 p-5">
                <div className="flex h-12 w-12 items-center justify-center bg-brand-black text-white">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-brand-black">Professionelt udtryk</h3>
                <p className="mt-2 text-sm leading-7 text-brand-gray-600">Resultatet skal være præcist, rent og visuelt stærkt – både tæt på og på afstand.</p>
              </article>

              <article className="border border-brand-gray-200 bg-brand-gray-50 p-5">
                <div className="flex h-12 w-12 items-center justify-center bg-brand-black text-white">
                  <Mail className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-brand-black">Direkte kontakt</h3>
                <p className="mt-2 text-sm leading-7 text-brand-gray-600">Du får klar kommunikation hele vejen gennem forløbet, så der ikke opstår tvivl om næste skridt.</p>
              </article>
            </div>

            <div className="mt-10 border border-brand-gray-200 bg-white p-6">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-orange">Kontaktinformation</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-brand-gray-500">Telefon</span>
                  <a href={company.phoneHref} className="mt-1 inline-flex text-sm font-bold text-brand-black whitespace-nowrap">
                    {company.phone}
                  </a>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-brand-gray-500">E-mail</span>
                  <a href={company.emailHref} className="mt-1 inline-flex text-sm font-bold text-brand-black whitespace-nowrap">
                    {company.email}
                  </a>
                </div>
                <div className="sm:col-span-2">
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-brand-gray-500">Adresse</span>
                  <p className="mt-1 text-sm font-bold text-brand-black">{company.address}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-black py-20 text-white sm:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-orange">Kontakt RS Byg</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Klar til at tale om dit projekt?
          </h2>
          <p className="mt-5 text-sm leading-7 text-brand-gray-200">
            Uanset om du skal bruge en ny terrasse, en renovering eller en større udskiftning, er du velkommen til at kontakte os for en professionel dialog om mulighederne.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/kontakt" className="inline-flex items-center justify-center bg-brand-orange px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-orange-hover">
              Gå til kontakt
              <ArrowRight className="ml-3 h-4 w-4" />
            </Link>
            <a href={company.phoneHref} className="inline-flex items-center justify-center border border-white/20 px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange whitespace-nowrap">
              Ring {company.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
